import { Eye, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/lib/class-control";

export interface IProjectPerson {
  id: number;
  name: string;
  avatarUrl?: string;
  phone: string;
  email: string;
  role: string;
  status: "Ativo" | "Inativo";
  registrationDate: string;
}

interface PeopleTableProps {
  people: IProjectPerson[];
  onView?: (id: number) => void;
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
}

export function PeopleTable({ people, onView, onEdit, onDelete }: PeopleTableProps) {
  const handleDelete = (id: number, name: string) => {
    if (onDelete && confirm(`Tem certeza que deseja remover ${name} do projeto?`)) {
      onDelete(id);
    }
  };

  return (
    <div className="flex flex-col gap-px items-start w-full">
      {/* Table Header */}
      <div className="bg-[#0C1427] box-border flex gap-[59px] h-[42px] items-center px-[20px] py-[10px] rounded-[8px] w-full">
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[160px]">
          Nome
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[160px]">
          Telefone
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[220px]">
          E-mail
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[151px]">
          Equipe/Função
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[120px]">
          Status
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[140px]">
          Data
        </p>
        <p className="font-['Plus_Jakarta_Sans'] font-bold leading-normal text-[#f6f6f6] text-[14px] w-[123px]">
          Ações
        </p>
      </div>

      {/* Table Rows */}
      {people.map((person, index) => (
        <div key={person.id}>
          <div className="box-border flex gap-[59px] items-center px-[20px] py-[10px] w-full">
            {/* Nome com Avatar */}
            <div className="box-border flex gap-[10px] items-center px-0 py-[10px] w-[160px]">
              <div className="relative shrink-0 w-[39px] h-[39px]">
                {person.avatarUrl ? (
                  <img
                    src={person.avatarUrl}
                    alt={person.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full rounded-full bg-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                    {person.name.charAt(0)}
                  </div>
                )}
              </div>
              <p className="font-['Plus_Jakarta_Sans'] font-semibold leading-normal text-[#f6f6f6] text-[16px] whitespace-pre">
                {person.name}
              </p>
            </div>

            {/* Telefone */}
            <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#f6f6f6] text-[16px] w-[160px]">
              {person.phone}
            </p>

            {/* E-mail */}
            <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#f6f6f6] text-[16px] w-[220px]">
              {person.email}
            </p>

            {/* Equipe/Função */}
            <div className="box-border flex gap-[10px] items-center px-0 py-[10px] w-[151px]">
              <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#f6f6f6] text-[16px] whitespace-pre">
                {person.role}
              </p>
            </div>

            {/* Status */}
            <div className="flex gap-[10px] items-center justify-center w-[120px]">
              <div
                className={cn(
                  "border border-solid box-border flex gap-[10px] h-[39px] items-center justify-center rounded-[10px] w-[94px]",
                  person.status === "Ativo"
                    ? "bg-[#2A0B6A] border-[#7846FF]"
                    : "bg-[#1a1a1a] border-[#5d5d5d]"
                )}
              >
                <p
                  className={cn(
                    "font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[16px] text-center whitespace-pre",
                    person.status === "Ativo" ? "text-[#A085FF]" : "text-[#888888]"
                  )}
                >
                  {person.status}
                </p>
              </div>
            </div>

            {/* Data */}
            <div className="box-border flex gap-[10px] items-center px-0 py-[10px] w-[140px]">
              <p className="font-['Plus_Jakarta_Sans'] font-normal leading-normal text-[#f6f6f6] text-[16px] whitespace-pre">
                {person.registrationDate}
              </p>
            </div>

            {/* Ações */}
            <div className="box-border flex gap-[10px] items-center p-[10px] rounded-[30px] w-[123px]">
              <button
                onClick={() => onView?.(person.id)}
                className={cn(
                  "overflow-clip shrink-0 w-[24px] h-[24px] transition-colors",
                  "hover:text-purple-400 text-gray-400"
                )}
                title="Visualizar"
              >
                <Eye className="w-full h-full" />
              </button>
              <button
                onClick={() => onEdit?.(person.id)}
                className={cn(
                  "shrink-0 w-[24px] h-[24px] transition-colors",
                  "hover:text-purple-400 text-gray-400"
                )}
                title="Editar"
              >
                <Pencil className="w-full h-full" />
              </button>
              <button
                onClick={() => handleDelete(person.id, person.name)}
                className={cn(
                  "shrink-0 w-[24px] h-[24px] transition-colors",
                  "hover:text-red-500 text-gray-400"
                )}
                title="Excluir"
              >
                <Trash2 className="w-full h-full" />
              </button>
            </div>
          </div>

          {/* Divider Line */}
          {index < people.length - 1 && (
            <div className="h-0 w-full relative">
              <div className="absolute bottom-0 left-0 right-0 top-[-1px] border-b border-[#2a2f4a]" />
            </div>
          )}
        </div>
      ))}

      {/* Empty State */}
      {people.length === 0 && (
        <div className="text-center py-12 w-full">
          <p className="text-gray-400 text-sm">Nenhuma pessoa encontrada</p>
        </div>
      )}
    </div>
  );
}
